/**
 * Canonical role + permission catalog for the Beauty Burst platform.
 * Permissions are checked server-side on every mutation.
 */
export const PERMISSIONS = [
  // Customer
  'customer.account.manage',
  'customer.cart.manage',
  'customer.order.place',
  'customer.order.read',
  'customer.refund.request',
  'customer.review.write',
  'customer.membership.manage',
  'customer.privacy.request',
  // Phase 3 customer surfaces. Favorites, lists, addresses and notification
  // preferences are separate permissions from the account hub so a future limited
  // session (for example a shared household device) can be granted browsing and
  // list access without exposing profile or privacy controls.
  'customer.favorites.manage',
  'customer.lists.manage',
  'customer.address.manage',
  'customer.notifications.manage',

  // Retailer
  'retailer.dashboard.read',
  'retailer.order.read',
  'retailer.order.manage',
  'retailer.catalog.read',
  'retailer.catalog.manage',
  'retailer.import.manage',
  'retailer.inventory.read',
  'retailer.inventory.manage',
  'retailer.earnings.read',
  'retailer.payout.read',
  'retailer.hours.manage',
  'retailer.overnight.manage',
  'retailer.promotion.manage',
  'retailer.team.manage',
  'retailer.integration.manage',
  'retailer.settings.manage',

  // Admin
  'admin.overview.read',
  'admin.retailers.read',
  'admin.retailers.manage',
  'admin.customers.read',
  'admin.customers.manage',
  'admin.catalog.manage',
  'admin.direct_inventory.manage',
  'admin.imports.manage',
  'admin.orders.read',
  'admin.orders.manage',
  'admin.delivery.manage',
  'admin.overnight.manage',
  'admin.refunds.manage',
  'admin.finance.read',
  'admin.finance.manage',
  'admin.payouts.manage',
  'admin.commission.manage',
  'admin.membership.manage',
  'admin.loyalty.manage',
  'admin.advertising.manage',
  'admin.reviews.manage',
  'admin.support.manage',
  'admin.reports.export',
  'admin.service_areas.manage',
  // Phase 3 acquisition data. Read and manage are separate because exporting
  // first-party lead data is a materially different act from editing a status.
  'admin.leads.read',
  'admin.leads.manage',
  'admin.analytics.read',
  'admin.discovery.manage',
  'admin.feature_flags.manage',
  'admin.integrations.manage',
  'admin.legal.manage',
  'admin.audit.read',
  'admin.settings.manage',

  // Courier integrations. A courier principal can act on the assignments it has
  // been granted and nothing else: it holds no catalogue, customer, or money
  // permission of any kind.
  'courier.assignment.manage',

  // System
  'system.job.run',

  // Session self-service. Reading your own identity (who am I, which roles and
  // permissions I hold, whether MFA is satisfied) is the baseline of every
  // authenticated surface. It is deliberately separate from customer.account.manage
  // so admin.owner — which intentionally holds no customer-scoped permission — can
  // still fetch its own profile after sign-in without bypassing the model.
  'auth.self.read',
] as const;

export type PermissionKey = (typeof PERMISSIONS)[number];

export const ROLES = [
  'customer',
  'retailer.owner',
  'retailer.manager',
  'retailer.staff',
  'retailer.finance',
  'admin.owner',
  'admin.operations',
  'admin.finance',
  'admin.support',
  'admin.readonly',
  'courier.integration',
  'system.worker',
] as const;

export type RoleKey = (typeof ROLES)[number];

const RETAILER_STAFF: PermissionKey[] = [
  'retailer.dashboard.read',
  'retailer.order.read',
  'retailer.order.manage',
  'retailer.catalog.read',
  'retailer.inventory.read',
];

const RETAILER_MANAGER: PermissionKey[] = [
  ...RETAILER_STAFF,
  'retailer.catalog.manage',
  'retailer.import.manage',
  'retailer.inventory.manage',
  'retailer.hours.manage',
  'retailer.overnight.manage',
  'retailer.promotion.manage',
];

const ADMIN_READONLY: PermissionKey[] = [
  'admin.overview.read',
  'admin.retailers.read',
  'admin.customers.read',
  'admin.orders.read',
  'admin.finance.read',
];

export const ROLE_PERMISSIONS: Record<RoleKey, readonly PermissionKey[]> = {
  customer: [
    'customer.account.manage',
    'customer.cart.manage',
    'customer.order.place',
    'customer.order.read',
    'customer.refund.request',
    'customer.review.write',
    'customer.membership.manage',
    'customer.privacy.request',
    'customer.favorites.manage',
    'customer.lists.manage',
    'customer.address.manage',
    'customer.notifications.manage',
  ],
  'retailer.staff': RETAILER_STAFF,
  'retailer.manager': RETAILER_MANAGER,
  'retailer.finance': [
    'retailer.dashboard.read',
    'retailer.earnings.read',
    'retailer.payout.read',
    'retailer.order.read',
  ],
  'retailer.owner': [
    ...RETAILER_MANAGER,
    'retailer.earnings.read',
    'retailer.payout.read',
    'retailer.team.manage',
    'retailer.integration.manage',
    'retailer.settings.manage',
  ],
  'admin.readonly': [...ADMIN_READONLY, 'admin.leads.read', 'admin.analytics.read'],
  'admin.support': [
    ...ADMIN_READONLY,
    'admin.support.manage',
    'admin.customers.manage',
    'admin.orders.manage',
    'admin.reviews.manage',
    'admin.leads.read',
    'admin.leads.manage',
  ],
  'admin.finance': [
    ...ADMIN_READONLY,
    'admin.finance.manage',
    'admin.payouts.manage',
    'admin.commission.manage',
    'admin.refunds.manage',
    'admin.reports.export',
    'admin.analytics.read',
  ],
  'admin.operations': [
    ...ADMIN_READONLY,
    'admin.retailers.manage',
    'admin.orders.manage',
    'admin.delivery.manage',
    'admin.overnight.manage',
    'admin.direct_inventory.manage',
    'admin.imports.manage',
    'admin.catalog.manage',
    'admin.service_areas.manage',
    'admin.support.manage',
    'admin.leads.read',
    'admin.leads.manage',
    'admin.analytics.read',
    'admin.discovery.manage',
  ],
  /**
   * Platform owner. Holds every admin-scoped permission, but deliberately holds
   * NO retailer-scoped or customer-scoped permission: a tenant's own data is
   * reached through admin.* oversight permissions, never by silently acting as
   * that tenant. Any future support-impersonation path must be its own audited
   * permission.
   */
  'admin.owner': [...PERMISSIONS].filter((p) => p.startsWith('admin.') && p !== 'system.job.run'),
  'system.worker': ['system.job.run'],
  /**
   * A delivery provider integration. Deliberately the narrowest role in the
   * platform: it can progress the assignments bound to it and read nothing else.
   */
  'courier.integration': ['courier.assignment.manage'],
};

/** Roles that require MFA once MFA enforcement is enabled (Phase 9 gate). */
export const MFA_REQUIRED_ROLES: readonly RoleKey[] = [
  'admin.owner',
  'admin.finance',
  'admin.operations',
  'retailer.owner',
];

export const RETAILER_SCOPED_ROLES: readonly RoleKey[] = [
  'retailer.owner',
  'retailer.manager',
  'retailer.staff',
  'retailer.finance',
];

export const ADMIN_ROLES: readonly RoleKey[] = [
  'admin.owner',
  'admin.operations',
  'admin.finance',
  'admin.support',
  'admin.readonly',
];

export function permissionsForRoles(roles: readonly RoleKey[]): PermissionKey[] {
  const set = new Set<PermissionKey>();
  for (const role of roles)
    for (const permission of ROLE_PERMISSIONS[role] ?? []) set.add(permission);
  // Every principal that can hold a session may read its own identity. This is
  // granted here rather than per-role so admin.owner — whose permission set is
  // derived as every `admin.*` key — still receives auth.self.read without being
  // given any customer-scoped permission. system.worker is a machine identity
  // with no session and is the only excluded role.
  if (roles.some((role) => role !== 'system.worker')) {
    set.add('auth.self.read');
  }
  return [...set].sort();
}
